import React from 'react';
import Header from '../Header';
import Footer from '../Footer';
import { CssBaseline, Box, useMediaQuery, Theme } from '@material-ui/core';
import { layoutsType } from '../../types/Layout';
import { isSideBarLayout, isSimplePageLayout, isSplitPageLayout } from './utils/detectLayoutType';
import AskPorterGrid from '../AskPorterGrid';
import { generateHeights } from './utils/calcs';
import { useTranslation } from 'react-i18next';
import { useConfig } from '../../providers/Config';

export interface LayoutProps {
  layout: layoutsType;
  showHeader?: boolean | 'simple';
  showFooter?: boolean | 'half';
  overflow?: 'auto' | 'none';
  simpleHeaderTitle?: string;
}

/**
 * Responsive Layout component which accepts components as params to produce a page with a header
 *
 * @param layout - the layout type as defined in 'layoutsType' - types/Layout.ts
 * @param showHeader - Optional, Accepts either a Boolean indicating whether the complete header (inc. nav) should be
 * shown, defaults true.
 * Also accepts a string 'simple' for a blank header with the tenant logo only.
 * @param showFooter - Optional, Accepts either a Boolean indicating whether the footer (inc. nav) should be shown, defaults
 * false. Also accepts a string 'half' for SplitPageLayout ONLY which indicates footer should be 50% of screen width.
 *
 * @returns Layout with responsive nav, footer (if specified) and supplied child component(s) as content
 */
const Layout: React.FC<LayoutProps> = ({
  showHeader = true,
  showFooter = false,
  layout,
  overflow = 'none',
  simpleHeaderTitle = '',
}: LayoutProps) => {
  const { footerHeight, headerHeight, constrainedHeight } = generateHeights({ showHeader, showFooter });
  const smUp = useMediaQuery((theme: Theme) => theme.breakpoints.up('sm'));
  const { t } = useTranslation();
  const contentComponent = isSimplePageLayout(layout) ? 'main' : 'div';
  const shouldConstrainHeight = isSideBarLayout(layout) && layout?.constrainHeight;
  const { tenantDisplay } = useConfig();
  const privacyPolicyUrl = tenantDisplay?.links?.privacyPolicyUrl;
  const termsOfUseUrl = tenantDisplay?.links?.termsOfUseUrl;

  /**
   * Identifies and renders the correct content based on the layout provided
   */
  const renderContent = () => {
    if (isSideBarLayout(layout)) {
      const mobileMain = layout.mobileMain ? layout.mobileMain : 'main';

      return (
        <Box
          sx={{
            width: { xxs: '100%', sm: '100%', md: '100%' },
            display: 'flex',
            flexDirection: 'row',
            height: '100%',
          }}
        >
          <Box
            component={smUp ? 'aside' : 'main'}
            sx={{
              width: { xxs: '100%', sm: '372px', md: '448px' },
              overflowY: layout?.constrainHeight ? 'auto' : undefined,
            }}
            bgcolor={!smUp && layout.flatMain ? '' : 'background.paper'}
          >
            <Box sx={{ height: layout?.constrainHeight ? '100%' : undefined }}>
              {smUp ? layout.aside : layout[mobileMain]}
            </Box>
          </Box>
          {smUp && (
            <Box
              component="main"
              p={6} // 24px(1.5rem) as per design
              borderLeft={1}
              borderColor="divider"
              sx={{ width: { xxs: '100%', sm: 'calc(100% - 372px)', md: 'calc(100% - 448px)' } }}
            >
              {layout.flatMain ? (
                layout.main
              ) : (
                <Box border={1} borderColor="divider" bgcolor="background.paper" height="100%">
                  {layout.main}
                </Box>
              )}
            </Box>
          )}
        </Box>
      );
    } else if (isSplitPageLayout(layout)) {
      const flexDirection = layout.flexDirection ? layout.flexDirection : 'row';

      return (
        <AskPorterGrid container item sm={12} minHeight="100%" flexDirection={flexDirection}>
          <AskPorterGrid item container component="main" xxs={12} sm={6} minHeight="100%">
            <Box width="100%" display="flex" flexDirection="column" flexWrap="nowrap">
              <Box flexGrow={1}>{layout.main}</Box>
              {/* If the footer is half then render it within this box so it inherits the parent width */}
              {showFooter === 'half' && renderFooter}
            </Box>
          </AskPorterGrid>
          {smUp && (
            <AskPorterGrid component="aside" item sm={6}>
              {layout.right}
            </AskPorterGrid>
          )}
        </AskPorterGrid>
      );
    } else if (isSimplePageLayout(layout)) return layout.main;

    // If the layout cannot be established return null to ensure nothing is rendered
    return null;
  };

  /**
   * Identifies and renders the correct footer
   */
  const renderFooter = (
    <AskPorterGrid item width="100%">
      <Footer
        height={footerHeight}
        t={t}
        privacyPolicyUrl={privacyPolicyUrl ? privacyPolicyUrl : undefined}
        termsOfUseUrl={termsOfUseUrl ? termsOfUseUrl : undefined}
      />
    </AskPorterGrid>
  );

  return (
    <React.Fragment>
      <CssBaseline />
      {/* The generic layout uses a grid flexed as cols, this stacks the header, content and footer.  */}
      <AskPorterGrid flexDirection="column" flexWrap="nowrap" height="100%" container position="relative">
        {showHeader && (
          <AskPorterGrid item borderBottom={1} borderColor="divider">
            <Header
              mode={showHeader === 'simple' ? 'simple' : 'complete'}
              simpleHeaderTitle={simpleHeaderTitle}
              height={headerHeight}
            />
          </AskPorterGrid>
        )}
        <AskPorterGrid
          container
          item
          flexDirection="row"
          flexGrow={1}
          overflow={overflow}
          component={contentComponent}
          height={shouldConstrainHeight ? constrainedHeight : undefined}
        >
          {renderContent()}
        </AskPorterGrid>
        {/* Render a footer only when footer is true, half footer is handled in the SplitPageLayout */}
        {showFooter === true && renderFooter}
      </AskPorterGrid>
    </React.Fragment>
  );
};

export default Layout;
